import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { metaByIdentifierSelector } from '../../Metas/selectors';
import {
  iosCourseTiers,
  PAID_COHORT_DEFAULT_CURRENCY,
  PAID_COHORT_IAP_CURRENCY,
} from '../../Courses/constants';

export const coursesListSelector = (state) => state.CoursesList;

const coursesSelector = (state) => state.Courses;
const cohortsSelector = (state) => state.Cohorts;

// selector to get courses list with list of specific type - paid, free, featured
export const makeCoursesListForTypesSelector = (types) =>
  createSelector(
    [coursesListSelector, coursesSelector, cohortsSelector],
    (courses, courseData, cohortData) => {
      const defaultTypes = types.size > 0 ? types : Immutable.fromJS(['list']);
      const coursesTypes = defaultTypes.map((type) => {
        const isDefaultType = !type || type === '' || type === 'list';
        const finalType = isDefaultType ? 'list' : type;
        return Immutable.fromJS({
          type: finalType,
          path: Immutable.fromJS(['coursesList', finalType]),
        });
      });
      const newObject = coursesTypes.reduce((current, value) => {
        let coursesList = courses.getIn(value.get('path'), Immutable.Map());
        if (coursesList.get('courses')) {
          coursesList.get('courses').forEach((course, index) => {
            if (course.get('paidCohort')) {
              // get default cohort path
              const cohortPath = courseData.getIn([
                course.get('path').toLowerCase(),
                'defaultCohort',
              ]);
              // get cohort data
              const cohort = cohortData.get(cohortPath);
              let currency =
                cohort.get('currency') || PAID_COHORT_DEFAULT_CURRENCY;
              let price = cohort.get('studentPrice');
              const iosProductId = cohort.get('iosProductId');
              if (iosProductId) {
                const productIdArray = iosProductId.split('.');
                const priceTier = productIdArray[productIdArray.length - 1];
                price = Immutable.fromJS(iosCourseTiers).get(priceTier, '');
                currency = PAID_COHORT_IAP_CURRENCY;
              }
              coursesList = coursesList
                .setIn(['courses', index, 'studentPrice'], price)
                .setIn(['courses', index, 'currency'], currency);
            }
          });
        }
        const result =
          coursesList.size > 0
            ? coursesList
            : Immutable.fromJS({
                courses: [],
                total: 0,
              });
        return current.set(value.get('type'), result);
      }, Immutable.Map());
      return newObject;
    }
  );

// selector to get courses fetching status with list of specific type - paid, free, featured
export const makeCoursesListFetchingStatusForTypesSelector = (types) =>
  createSelector(
    [coursesListSelector, metaByIdentifierSelector],
    (courses, meta) => {
      const defaultTypes = types.size > 0 ? types : Immutable.fromJS(['list']);
      const coursesTypes = defaultTypes.map((type) => {
        const isDefaultType = !type || type === '' || type === 'list';
        const finalType = isDefaultType ? 'list' : type;
        return Immutable.fromJS({
          type: finalType,
          path: Immutable.fromJS(['coursesList', 'fetching', finalType]),
        });
      });
      const newObject = coursesTypes.reduce((current, value) => {
        const path = value.get('path');
        let status = courses.getIn(
          path,
          Immutable.Map({
            requested: false,
          })
        );
        switch (path.get(2)) {
          case 'free': {
            const freeCourseListStatus = meta.get(
              'freeCourseList',
              Immutable.Map({
                loading: false,
                error: false,
              })
            );
            status = status.set('loading', freeCourseListStatus.get('loading'));
            status = status.set('error', freeCourseListStatus.get('error'));
            break;
          }
          case 'paid': {
            const paidCourseListStatus = meta.get(
              'paidCourseList',
              Immutable.Map({
                loading: false,
                error: false,
              })
            );
            status = status.set('loading', paidCourseListStatus.get('loading'));
            status = status.set('error', paidCourseListStatus.get('error'));
            break;
          }
          case 'featured': {
            const featuredCourseListStatus = meta.get(
              'featuredCourseList',
              Immutable.Map({
                loading: false,
                error: false,
              })
            );
            status = status.set(
              'loading',
              featuredCourseListStatus.get('loading')
            );
            status = status.set('error', featuredCourseListStatus.get('error'));
            break;
          }
          default: {
            break;
          }
        }
        return current.set(value.get('type'), status);
      }, Immutable.Map());
      return newObject;
    }
  );

// selector to get course filter with specific type - categories, etc
export const makeCourseFilterSelector = (type, returnCollection) =>
  createSelector(
    [coursesListSelector],
    (courses) => {
      if (!type) {
        throw Error('"type" is missing from makeCourseFilterSelector');
      }
      const filters = courses.getIn(
        ['coursesList', 'filters', type],
        Immutable.List()
      );
      if (returnCollection) {
        return filters;
      }
      return filters.reduce((current, value) => {
        return current.push(value.get('value'));
      }, Immutable.List());
    }
  );

export const coursesQueryTextSelector = createSelector(
  [coursesListSelector],
  (courses) => {
    return courses.getIn(['coursesList', 'queryText'], '');
  }
);

export const featureInstitutionsFilterSelector = createSelector(
  [coursesListSelector],
  (courses) => {
    return courses.getIn(
      ['coursesList', 'filters', 'institutionPaths'],
      Immutable.List()
    );
  }
);

export const myCoursesQueryTextSelector = createSelector(
  [coursesListSelector],
  (courses) => {
    return courses.getIn(['coursesList', 'myCoursesQueryText'], '');
  }
);

export const feedSearchBarUserInputSelector = createSelector(
  [coursesListSelector],
  (courses) => {
    return courses
      ? courses.getIn(['coursesList', 'feedSearchBarUserInput'], '')
      : '';
  }
);

export const makeCoursesListDetailByCoursePathSelector = (
  courseOutcomeObjective
) =>
  createSelector(
    [coursesSelector, cohortsSelector],
    (courses, cohorts) => {
      let courseInfo = Immutable.Map();
      let cohortInfo = Immutable.Map();
      const path = courseOutcomeObjective.get('path').toLowerCase();
      if (courses.size > 0) {
        courseInfo = courses.get(path);
        if (cohorts.size > 0) {
          cohortInfo = cohorts.get(courseInfo.get('defaultCohort'));
        }
      }
      return courseOutcomeObjective.merge(courseInfo, cohortInfo);
    }
  );
